import {capitalize} from 'lodash';
import {useCallback, useMemo} from 'react';
import CAMPAIGN_STATE from '@/config/campaignState';
import {CampaignProps} from '@/types/campaign';
import campaignFetchers from '@/api/fetchers/campaign';
import useCampaignBrandActionItemsQuery from '@/hooks/queries/use-campaign-brand-action-items-query';
import useDataQuery from '@/hooks/use-data-query';

type UseCampaignQueryProps = {
  campaignId?: string;
  include?: string[];
  fetchBrandActionItems?: boolean;
  enabled?: boolean;
  retry?: boolean;
  refetchOnWindowFocus?: boolean;
  staleTime?: number;
  onError?: (error: any) => void;
};

type UseCampaignQueryReturn = {
  campaign: CampaignProps;
  data?: any;
  isLoading?: boolean;
  isError?: boolean;
  isFetched?: boolean;
  isFetching?: boolean;
  error?: any;
  refetch?: () => void;
  [key: string]: any;
};

function useCampaignQuery({
  campaignId,
  include,
  fetchBrandActionItems = false,
  enabled = true,
  retry = true,
  refetchOnWindowFocus = false,
  staleTime = 0,
  onError,
}: UseCampaignQueryProps): UseCampaignQueryReturn {
  const queryKey = include
    ? ['campaign', campaignId, {include}]
    : ['campaign', campaignId];
  const campaignQueryResponse = useDataQuery<CampaignProps>({
    queryKey,
    queryFn: campaignFetchers.campaign,
    enabled: !!campaignId && enabled,
    retry,
    onError,
    staleTime,
    refetchOnWindowFocus,
    meta: {
      id: campaignId,
      include,
    },
  });

  const {data: campaignBrandActionItems} = useCampaignBrandActionItemsQuery({
    campaignId: campaignId || '',
    enabled: fetchBrandActionItems,
  });

  const campaign = useCallback((): Partial<CampaignProps> => {
    return campaignQueryResponse.data || {};
  }, [campaignQueryResponse.data]);

  const showCampaignDetails = () => {
    const {state} = campaign();
    const blacklist = [
      CAMPAIGN_STATE.UNDER_CONSTRUCTION,
      CAMPAIGN_STATE.DRAFT,
      CAMPAIGN_STATE.UNDER_REVIEW,
    ];

    if (!state) {
      return false;
    }

    return !blacklist.includes(state);
  };

  const networkDasherized = () => {
    const campaignNetwork = campaign().network;
    if (campaignNetwork) {
      return campaignNetwork.replaceAll('_', '-');
    }

    return campaignNetwork;
  };

  const isBcaCompatible = () => {
    const campaignNetwork = campaign().network;

    if (!campaignNetwork) {
      return true;
    }
    return campaignNetwork === 'instagram' || campaignNetwork === 'facebook';
  };

  const campaignNetworkPrettyName = () => {
    const campaignNetwork = campaign().network;

    switch (campaignNetwork) {
      case 'youtube':
        return 'YouTube';
      case 'tiktok':
        return 'TikTok';
      case 'tiktok_creator_marketplace':
        return 'TikTok Creator Marketplace';
      case 'tiktok_v2':
        return 'TikTok';
      default:
        return campaignNetwork ? capitalize(campaignNetwork) : campaignNetwork;
    }
  };

  const invitesRemaining = () => {
    const {maxInviteCount = 0, currentInviteCount = 0} = campaign();
    return maxInviteCount - currentInviteCount;
  };

  const validForActionItems = () => {
    return ['accepting_proposals', 'closed_to_new_proposals'].includes(
      campaign().state || ''
    );
  };

  const totalFeePercentage = () => {
    const {
      otherFeePercentage = 0,
      serviceFeePercentage = 0,
      fixedPercentageMargin = 0,
    } = campaign();

    return fixedPercentageMargin || otherFeePercentage + serviceFeePercentage;
  };

  const desiredStatsSocialStream = () => {
    const {
      requiredFeedPostsCount = 0,
      requiredReelsPostsCount = 0,
      requiredStoryPostsCount = 0,
    } = campaign();

    return (
      (requiredFeedPostsCount > 0 && 'feed') ||
      (requiredStoryPostsCount > requiredReelsPostsCount && 'story') ||
      (requiredReelsPostsCount > 0 && 'reels') ||
      'feed'
    );
  };

  const campaignType = () => {
    const {network, collective} = campaign();
    // investigate the presence of collective and where this computed prop is used
    // was broken on campaign-details and preview-and-submit components
    if (collective?.id && !network) {
      return 'closed-network';
    }

    return networkDasherized() || 'content-creation';
  };

  const validForInviteActionItem = () => {
    const campaignData = campaign();
    const {state, brandActionItemsGroup} = campaignData;
    const remainingInvites = invitesRemaining();

    return (
      state === CAMPAIGN_STATE.ACCEPTING_PROPOSALS &&
      remainingInvites > 0 &&
      brandActionItemsGroup?.applicationsCount === 0 &&
      (brandActionItemsGroup?.creatorSuggestionsCount ?? 0) > 0
    );
  };

  const hasFulfillment = () => {
    const {deliverablesType} = campaign();
    return !!deliverablesType && deliverablesType.trim() !== '';
  };

  const hasRemainingInvites = () => {
    return invitesRemaining() > 0;
  };

  const noAdManager = () => {
    const {adNetworkAccount} = campaign();
    return !adNetworkAccount?.id;
  };

  const isMarginBased = () => {
    const {fixedPercentageMargin = 0} = campaign();
    return fixedPercentageMargin > 0;
  };

  const isUnderReview = () => {
    const {state} = campaign();
    return state === CAMPAIGN_STATE.UNDER_REVIEW;
  };

  const hasUnreadConversations = () => {
    const {unreadConversationsCount = 0} = campaign();
    return unreadConversationsCount > 0;
  };

  const blogUploadRequired = () => {
    const {blogsToUpload = 0} = campaign();
    return blogsToUpload > 0;
  };

  const mixedMediaUploadRequired = () => {
    const {mixedMediaToUpload = 0} = campaign();
    return mixedMediaToUpload > 0;
  };

  const photoPostRequired = () => {
    const {requiredFeedPostsCount = 0} = campaign();
    return requiredFeedPostsCount > 0;
  };

  const reelsPostRequired = () => {
    const {requiredReelsPostsCount = 0} = campaign();
    return requiredReelsPostsCount > 0;
  };

  const storyPostRequired = () => {
    const {requiredStoryPostsCount = 0} = campaign();
    return requiredStoryPostsCount > 0;
  };

  const photoUploadRequired = () => {
    const {photosToUpload = 0} = campaign();
    return photosToUpload > 0;
  };

  const videoUploadRequired = () => {
    const {videosToUpload = 0} = campaign();
    return videosToUpload > 0;
  };

  const brandActionItemsGroup = () => {
    return campaignBrandActionItems || {};
  };

  const result = useMemo(
    () => ({
      ...campaignQueryResponse,
      campaign: {
        ...campaign(),
        loaded: enabled && !!campaignId && campaignQueryResponse.isFetched,
        showCampaignDetails: showCampaignDetails(),
        networkDasherized: networkDasherized(),
        isBcaCompatible: isBcaCompatible(),
        campaignNetworkPrettyName: campaignNetworkPrettyName(),
        campaignType: campaignType(),
        invitesRemaining: invitesRemaining(),
        validForActionItems: validForActionItems(),
        validForInviteActionItem: validForInviteActionItem(),
        totalFeePercentage: totalFeePercentage(),
        desiredStatsSocialStream: desiredStatsSocialStream(),
        hasFulfillment: hasFulfillment(),
        hasRemainingInvites: hasRemainingInvites(),
        noAdManager: noAdManager(),
        isMarginBased: isMarginBased(),
        isUnderReview: isUnderReview(),
        hasUnreadConversations: hasUnreadConversations(),
        blogUploadRequired: blogUploadRequired(),
        mixedMediaUploadRequired: mixedMediaUploadRequired(),
        photoPostRequired: photoPostRequired(),
        reelsPostRequired: reelsPostRequired(),
        storyPostRequired: storyPostRequired(),
        photoUploadRequired: photoUploadRequired(),
        videoUploadRequired: videoUploadRequired(),
        brandActionItemsGroup: brandActionItemsGroup(),
      } as CampaignProps,
    }),
    [
      campaignQueryResponse.data,
      enabled,
      campaignId,
      campaignQueryResponse.isFetched,
      campaignBrandActionItems,
    ]
  );

  return result;
}

export default useCampaignQuery;
